/**
 * The transport contract every chat backend satisfies.
 *
 * Capability flags drive UI affordances. The playground renders no approval UI
 * because its transport declares `approvals: false` — never because a component
 * asks which surface it is on. A surface check anywhere under `features/chat/`
 * defeats the entire abstraction.
 */
import type { ChatEvent } from './events';
import type { ChatMessage, ContentPart } from './message';

/** The content kinds a transport will carry. */
export type ContentKind = ContentPart['type'];

export interface ChatCapabilities {
  /** Emits reasoning and tool-call steps, not just assistant text. */
  steps: boolean;
  /** Can pause mid-run for a human decision. */
  approvals: boolean;
  /** Accepts image/audio content parts on input. */
  multimodalInput: boolean;
  cancel: boolean;
  /** The caller chooses the model per turn. */
  modelPicker: boolean;
  /**
   * Which content kinds the input offers. Narrower than `multimodalInput`,
   * which only says whether *any* attachment is possible: a surface may take
   * images but not audio, and the file picker should say so rather than accept
   * a file the transport will not send.
   */
  accept: ContentKind[];
}

export interface ChatInput {
  content: ContentPart[];
  conversationId?: string;
  /** A2A session continuity. Set on the first turn, then stable. */
  contextId?: string;
  /** Only meaningful when `capabilities.modelPicker`. */
  model?: string;
  /**
   * The turns preceding this one.
   *
   * Transports with a server-side session (A2A, keyed by `contextId`) ignore
   * this — the server already has the history, and re-sending it would duplicate
   * the conversation. Stateless completion APIs cannot hold a conversation
   * without it.
   */
  history?: ChatMessage[];
}

/**
 * Async iterables, not Observables: `for await` composes with Vue's reactivity
 * directly, and pulling in rxjs for a single stream would be the tail wagging
 * the dog.
 */
export interface ApprovalResponse {
  taskId: string;
  toolCallId: string;
  approved: boolean;
  /** asab-agents logs this against the approval; it does not route on it. Sent
   *  so an operator reading the logs can tell what was approved. */
  toolName?: string;
  reason?: string;
}

export interface ToolResultSubmission {
  taskId: string;
  toolCallId: string;
  output: string;
  toolName?: string;
  isError?: boolean;
}

export interface ChatTransport {
  readonly capabilities: ChatCapabilities;
  send(input: ChatInput, signal: AbortSignal): AsyncIterable<ChatEvent>;
  respondToApproval?(a: ApprovalResponse): Promise<void>;
  submitToolResult?(r: ToolResultSubmission): Promise<void>;
  cancel?(taskId: string): Promise<void>;
}

/**
 * Asserts a transport actually implements what its capabilities advertise.
 *
 * `approvals: true` without `respondToApproval` is a bug the type system cannot
 * catch — the flag is a value, the method is optional, and nothing ties them.
 * Checking at wire-up time fails loudly during development instead of stranding
 * a user at a live approval prompt that no button can resolve.
 */
export function assertTransportHonoursCapabilities(t: ChatTransport): void {
  const missing: string[] = [];
  if (t.capabilities.approvals && !t.respondToApproval) missing.push('respondToApproval');
  if (t.capabilities.approvals && !t.submitToolResult) missing.push('submitToolResult');
  if (t.capabilities.cancel && !t.cancel) missing.push('cancel');
  if (missing.length > 0) {
    throw new Error(
      `chat transport declares capabilities it does not implement: ${missing.join(', ')}`,
    );
  }
}
